import { Button, FormRow, FormSet, Input, P } from '@dnb/eufemia';
import { check_medium as CheckMediumIcon } from '@dnb/eufemia/icons';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { hasOwnProperty } from '@portals/shared-frontend/utils';
import { type JSX, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { type User, verifyMfaLogin } from '@/auth/cognito';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import AuthContext from '@/hooks/AuthContext';
import useReturnTo from '@/hooks/useReturnTo';

type VerifyMfaProps = {
  cognitoUser?: User;
  toggleView: () => void;
};

const mfaFormSchema = z.object({
  code: z.string().min(1, 'Filed cannot be empty'),
});

export default function VerifyMfa({ cognitoUser, toggleView }: VerifyMfaProps): JSX.Element {
  const [returnTo, , clearReturnTo] = useReturnTo();
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useContext(AuthContext);

  const {
    controller: { setError, setFormError, formError },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(mfaFormSchema, {});

  const onSubmit = handleSubmit(
    useCallback(
      async ({ code }) => {
        try {
          if (cognitoUser) {
            const user = await verifyMfaLogin({
              user: cognitoUser,
              code: code,
              type: 'MFA',
            });

            if (user?.challengeName === 'CUSTOM_CHALLENGE') {
              setError('code', 'Invalid verification code');
              return;
            }

            await refreshAuth();
            navigate(returnTo ?? '/');
            clearReturnTo();
          }
        } catch (error) {
          if (!hasOwnProperty(error, 'code')) {
            throw error;
          }

          switch (error.code) {
            case 'NotAuthorizedException': {
              setFormError(
                'You have reached the maximum number of allowed attempts. Please start the log-in process again.',
              );
              break;
            }
            default: {
              throw error;
            }
          }
        }
      },
      [clearReturnTo, cognitoUser, navigate, returnTo, setError, setFormError],
    ),
  );

  return (
    <ModalPage
      description="You can find this code in the same authenticator app you used to enable multi-factor authentication."
      subtitle="Log in"
      title="Fill in the multi-factor verification code"
    >
      {submitting && <LoadingModal />}
      <Container size="small">
        <FormSet on_submit={onSubmit} vertical>
          <FormRow>
            <Input
              autoFocus
              label="Verification code"
              placeholder="xxxxxx"
              size="large"
              stretch
              {...register.input('code')}
            />
          </FormRow>

          <FormRow top="large">
            <Button
              disabled={submitting}
              icon={CheckMediumIcon}
              icon_position="left"
              icon_size="medium"
              size="large"
              status={formError}
              type="submit"
            >
              Verify
            </Button>
          </FormRow>

          <FormRow top="medium">
            <P>
              Do not have your authenticator app available now? Then please log in with the recovery codes you received
              when you enabled multi-factor authentication.
            </P>

            <Button bounding on_click={toggleView} size="small" top="x-small" variant="tertiary">
              Log in with recovery codes
            </Button>
          </FormRow>
        </FormSet>
      </Container>
    </ModalPage>
  );
}
